'use client'

import Lightbox, {
  type Render,
  type ControllerRef,
  type Slide,
  type CustomAssetSlide,
} from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import Counter from 'yet-another-react-lightbox/plugins/counter'
import 'yet-another-react-lightbox/plugins/counter.css'
import {HgButton} from '~/design-system/hg/components'
import {useRef} from 'react'
import {type OmitStrict} from 'type-zoo'
import {cn} from '~/design-system/utils'
import {tv} from 'tailwind-variants'
import {grid, gridItem} from '~/design-system/foundations'

type HgLightboxProps = {
  index?: number
  isOpen: boolean
  closeLightbox: () => void
  slides: Slide[]
  render?: OmitStrict<Render, 'buttonClose' | 'buttonPrev' | 'buttonNext'>
}

function isCustomAssetSlide(slide: Slide): slide is CustomAssetSlide {
  return slide.type === 'custom-asset'
}

const wrapperAssetVariants = tv({
  base: 'grid min-h-0',
  variants: {
    aspectRatio: {
      '16:9': 'w-[min(100cqw,100cqh*16/9)] [aspect-ratio:16/9]',
      '4:5': 'w-[min(100cqw,100cqh*4/5)] [aspect-ratio:4/5]',
      '5:4': 'w-[min(100cqw,100cqh*5/4)] [aspect-ratio:5/4]',
      '3:2': 'w-[min(100cqw,100cqh*3/2)] [aspect-ratio:3/2]',
      '2:3': 'w-[min(100cqw,100cqh*2/3)] [aspect-ratio:2/3]',
      '1:1': 'w-[min(100cqw,100cqh)] [aspect-ratio:1/1]',
      '9:16': 'w-[min(100cqw,100cqh*9/16)] [aspect-ratio:9/16]',
    },
  },
})

const HgCustomAssetSlide = ({
  slide,
  rect,
}: {
  slide: CustomAssetSlide
  rect: {width: number; height: number}
}) => {
  if (!isCustomAssetSlide(slide)) {
    return undefined
  }

  const {asset, caption, credit, aspectRatio} = slide

  return (
    <div className="relative" style={{width: rect.width, height: rect.height}}>
      <div className="mt-[calc((var(--s3)_*_2_+_42px))] h-[calc(100%_-_(var(--s3)_*_2_+_42px))] max-h-[calc(100%_-_(var(--s3)_*_2_+_42px))]">
        <div className="flex h-full max-h-full w-full flex-col justify-between">
          <div className="flex h-full max-h-full min-h-0 w-full flex-1 items-center justify-center py-s6">
            <div className="flex h-full max-h-full w-full flex-col items-center justify-center px-s5 lg:px-s10">
              <div className="resize-both flex h-full w-full flex-col items-center justify-center overflow-auto [container-type:_size]">
                <div className={wrapperAssetVariants({aspectRatio})}>{asset}</div>
              </div>
            </div>
          </div>
          <div
            className={grid({
              class:
                'mx-auto my-s3 h-fit pb-[calc(var(--s6)+42px)] arcadia-body-3 md:min-h-[42px] md:pb-0',
            })}
          >
            <div className={gridItem({size: 'heading', class: 'flex'})}>
              <div className="flex flex-col flex-wrap items-center justify-center text-center 3xl:flex-row">
                <p className="inline-flex text-text-default">{caption}</p>
                {credit && (
                  <>
                    {caption && (
                      <span className="mx-s2 hidden text-border-subdued 3xl:inline-flex">
                        |
                      </span>
                    )}
                    <p className="inline-flex text-text-subdued">{credit}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const HgLightbox = ({
  index,
  isOpen,
  closeLightbox,
  slides,
  render,
}: HgLightboxProps) => {
  const lightboxRef = useRef<ControllerRef | null>(null)
  const hasSingleSlide = slides.length === 1
  const plugins = hasSingleSlide ? [] : [Counter]

  return (
    <Lightbox
      index={index || 0}
      controller={{ref: lightboxRef}}
      className="text-text-default arcadia-body-3"
      slides={slides}
      open={isOpen}
      close={closeLightbox}
      plugins={[...plugins]}
      carousel={{preload: 1, padding: 0, spacing: 0}}
      render={{
        ...render,
        buttonClose: () => (
          <HgButton
            key="close"
            onClick={() => lightboxRef.current?.close()}
            variant="tonal"
            contentType="icon"
            iconProps={{iconType: 'xmark'}}
            className="mx-[calc(var(--s5)_-_8px)] my-s2"
            aria-label="Close"
          />
        ),
        buttonPrev: () => (
          <HgButton
            key="prev"
            onClick={() => lightboxRef.current?.prev()}
            variant="tonal"
            contentType="icon"
            iconProps={{iconType: 'chevron-left'}}
            aria-label="Previous"
            className={cn(
              'absolute bottom-0 right-0 my-s3 ml-s6 mr-[calc(var(--s5)_+_var(--s2)+_42px)]',
              hasSingleSlide && 'hidden'
            )}
          />
        ),
        buttonNext: () => (
          <HgButton
            key={'next'}
            onClick={() => lightboxRef.current?.next()}
            variant="tonal"
            contentType="icon"
            iconProps={{iconType: 'chevron-right'}}
            aria-label="Next"
            className={cn(
              'absolute bottom-0 right-0 my-s3 mr-s5',
              hasSingleSlide && 'hidden'
            )}
          />
        ),
        slide: HgCustomAssetSlide,
      }}
      styles={{
        container: {backgroundColor: 'var(--background-default)'},
      }}
      counter={{
        separator: '/',
        container: {
          className:
            'arcadia-ui-2 text-text-default p-0 h-40 min-h-40 inline-flex items-center justify-center my-s3 ml-s5 mr-s6',
          style: {
            top: 'unset',
            filter: 'unset',
            bottom: 0,
            color: 'var(--text-default)',
          },
        },
      }}
    />
  )
}
